import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Legal.css';

const Features: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back to Home
        </button>

        <h1>Features</h1>
        <p className="last-updated">Everything you need to create professional storybooks</p>

        <section className="legal-section">
          <h2>🎨 AI-Powered Character Generation</h2>
          <p>
            Create unique, consistent characters using advanced AI technology. Our character generation 
            system ensures your characters maintain the same appearance throughout your entire storybook.
          </p>
          <ul>
            <li><strong>Natural Language Descriptions:</strong> Describe characters in plain English (e.g., "a friendly robot with blue eyes")</li>
            <li><strong>Multiple Angles:</strong> Generate characters in 4 or 8 directional views for dynamic storytelling</li>
            <li><strong>Style Matching:</strong> Characters automatically match your chosen art style</li>
            <li><strong>Instant Generation:</strong> Powered by GPT-Image-1-Mini for fast, high-quality results</li>
            <li><strong>Unlimited Characters:</strong> Create as many characters as your story needs</li>
            <li><strong>Character Library:</strong> All generated characters saved for reuse across projects</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>🖼️ Scene & Background Generation</h2>
          <p>
            Generate atmospheric backgrounds and complete scenes to bring your storybook world to life.
          </p>
          <ul>
            <li><strong>AI Scene Creation:</strong> Generate custom backgrounds from text descriptions</li>
            <li><strong>Various Settings:</strong> Indoor, outdoor, fantasy, realistic - any environment you need</li>
            <li><strong>Consistent Style:</strong> Backgrounds match your storybook's art style</li>
            <li><strong>Asset Integration:</strong> Combine generated backgrounds with characters seamlessly</li>
            <li><strong>Custom Colors:</strong> Add solid colors or gradients as simple backgrounds</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>✍️ AI Story Planning</h2>
          <p>
            Let AI help you develop compelling narratives with our intelligent plot planning system.
          </p>
          <ul>
            <li><strong>Plot Generation:</strong> AI suggests page-by-page storylines based on your concept</li>
            <li><strong>Story Structure:</strong> Automatic narrative arc with beginning, middle, and end</li>
            <li><strong>Customizable Tone:</strong> Choose from heartwarming, exciting, educational, funny, and more</li>
            <li><strong>Story Types:</strong> Fairy tales, adventures, educational books, bedtime stories, etc.</li>
            <li><strong>Editable Plots:</strong> Refine AI suggestions to match your creative vision</li>
            <li><strong>Auto-populate:</strong> Add plot text to pages automatically</li>
            <li><strong>Context Aware:</strong> AI considers your book description and page count</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>🎭 Visual Page Editor</h2>
          <p>
            Design beautiful storybook pages with our intuitive drag-and-drop editor. No design experience required.
          </p>
          <ul>
            <li><strong>Drag-and-Drop Interface:</strong> Click and drag elements to position them exactly where you want</li>
            <li><strong>Text Boxes:</strong> Add and format text with full typography controls</li>
            <li><strong>Font Options:</strong> Multiple font families, sizes, weights, and colors</li>
            <li><strong>Text Alignment:</strong> Left, center, right, and justify alignment options</li>
            <li><strong>Image Controls:</strong> Resize, rotate, and flip images freely</li>
            <li><strong>Layer Management:</strong> Control element stacking with bring forward/send backward</li>
            <li><strong>Selection Tools:</strong> Click to select and edit any element</li>
            <li><strong>Real-time Preview:</strong> See exactly how your page will look</li>
            <li><strong>Undo/Redo:</strong> Easily revert changes (coming soon)</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>📖 Multi-Page Management</h2>
          <p>
            Create storybooks with as many pages as you need, with tools to maintain consistency throughout.
          </p>
          <ul>
            <li><strong>Flexible Page Count:</strong> Create books from 1 to 100+ pages</li>
            <li><strong>Page Navigation:</strong> Quick thumbnails for easy navigation between pages</li>
            <li><strong>Add/Remove Pages:</strong> Dynamically adjust your book length</li>
            <li><strong>Page Reordering:</strong> Drag pages to rearrange story sequence</li>
            <li><strong>Duplicate Pages:</strong> Copy page layouts to maintain consistency</li>
            <li><strong>Page Templates:</strong> Save common layouts for reuse</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>🎯 Format Options</h2>
          <p>
            Choose the perfect format for your storybook project.
          </p>
          <ul>
            <li><strong>Portrait Format:</strong> Traditional book orientation (500-900px width)</li>
            <li><strong>Landscape Format:</strong> Wide format perfect for visual storytelling (800-900px width)</li>
            <li><strong>Square Format:</strong> Instagram-friendly format (600px × 600px)</li>
            <li><strong>Custom Sizes:</strong> Set specific dimensions for special projects</li>
            <li><strong>Format Locking:</strong> Prevent accidental resizing once set</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>🎨 Art Style Selection</h2>
          <p>
            Choose from 30+ art styles to give your storybook a unique visual identity.
          </p>
          <ul>
            <li><strong>Children's Book Illustration:</strong> Classic storybook style</li>
            <li><strong>Watercolor:</strong> Soft, painterly aesthetic</li>
            <li><strong>Digital Art:</strong> Modern, crisp illustrations</li>
            <li><strong>Studio Ghibli Style:</strong> Anime-inspired whimsical art</li>
            <li><strong>Disney/Pixar Style:</strong> 3D animated movie aesthetic</li>
            <li><strong>Manga/Anime:</strong> Japanese comic book style</li>
            <li><strong>Realistic:</strong> Photo-realistic rendering</li>
            <li><strong>And many more:</strong> Pencil sketch, oil painting, flat design, minimalist, retro, cyberpunk, and more</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>💾 Export & Publishing</h2>
          <p>
            Export your completed storybook in formats ready for professional publishing.
          </p>
          <ul>
            <li><strong>High-Resolution Export:</strong> Print-quality image output</li>
            <li><strong>PDF Generation:</strong> Create complete PDF files for printing</li>
            <li><strong>Individual Pages:</strong> Export pages separately for flexibility</li>
            <li><strong>KDP-Ready Format:</strong> Meets Amazon Kindle Direct Publishing specifications</li>
            <li><strong>Cover Page Export:</strong> Generate front and back covers</li>
            <li><strong>Multiple Formats:</strong> PDF, PNG, JPEG options</li>
            <li><strong>Batch Export:</strong> Export all pages at once</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>☁️ Cloud Storage & Security</h2>
          <p>
            Your work is safely stored in the cloud with enterprise-grade security.
          </p>
          <ul>
            <li><strong>Auto-Save:</strong> Work is automatically saved as you create</li>
            <li><strong>Cloud Backup:</strong> Projects stored securely in Google Cloud Platform</li>
            <li><strong>Cross-Device Access:</strong> Work from any computer or tablet</li>
            <li><strong>Version History:</strong> Track changes and restore previous versions (coming soon)</li>
            <li><strong>Secure Storage:</strong> Encrypted data transmission and storage</li>
            <li><strong>Asset Library:</strong> All your generated images saved and organized</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>💰 Flexible Pricing</h2>
          <p>
            Pay only for what you use with our token-based system.
          </p>
          <ul>
            <li><strong>Token Packages:</strong> Buy credits in bundles that fit your needs</li>
            <li><strong>No Subscriptions:</strong> No monthly fees - tokens never expire</li>
            <li><strong>Transparent Pricing:</strong> Know exactly what each generation costs</li>
            <li><strong>Bulk Discounts:</strong> Save more with larger token packages</li>
            <li><strong>Stripe Integration:</strong> Secure payment processing</li>
            <li><strong>Refund Policy:</strong> Clear terms for digital services</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>👥 User Management</h2>
          <p>
            Manage your account and track your creative work.
          </p>
          <ul>
            <li><strong>Secure Authentication:</strong> JWT-based login with password hashing</li>
            <li><strong>Google OAuth:</strong> Sign in with your Google account</li>
            <li><strong>Project Dashboard:</strong> View and manage all your storybooks</li>
            <li><strong>Token Balance:</strong> Track your remaining AI generation credits</li>
            <li><strong>Usage History:</strong> See your token consumption over time</li>
            <li><strong>Account Settings:</strong> Update profile and preferences</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>🚀 Performance & Reliability</h2>
          <p>
            Built on modern technology for speed and reliability.
          </p>
          <ul>
            <li><strong>Fast Generation:</strong> Characters and scenes created in seconds</li>
            <li><strong>Responsive Editor:</strong> Smooth drag-and-drop performance</li>
            <li><strong>Cloud Run Hosting:</strong> Scalable infrastructure that grows with demand</li>
            <li><strong>99.9% Uptime:</strong> Built on Google Cloud Platform</li>
            <li><strong>Global CDN:</strong> Fast asset loading worldwide</li>
            <li><strong>Regular Backups:</strong> Your data is safe and recoverable</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>📱 Coming Soon</h2>
          <p>
            We're constantly improving! Here's what's in development:
          </p>
          <ul>
            <li><strong>Mobile App:</strong> Create on your phone or tablet</li>
            <li><strong>Collaboration:</strong> Work on projects with others</li>
            <li><strong>Animation:</strong> Add simple animations to digital versions</li>
            <li><strong>Audio Narration:</strong> Record or generate voiceovers</li>
            <li><strong>Templates:</strong> Pre-designed page layouts and story templates</li>
            <li><strong>Public Gallery:</strong> Share your published works</li>
            <li><strong>Advanced Editing:</strong> More text effects and image filters</li>
            <li><strong>Batch Operations:</strong> Apply changes across multiple pages</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>🎓 Educational Use</h2>
          <p>
            Perfect for educators, parents, and students.
          </p>
          <ul>
            <li><strong>Classroom Projects:</strong> Students can create their own stories</li>
            <li><strong>Educational Content:</strong> Generate teaching materials and illustrated lessons</li>
            <li><strong>Language Learning:</strong> Create bilingual or multi-language books</li>
            <li><strong>Special Education:</strong> Customize content for individual learning needs</li>
            <li><strong>Literacy Development:</strong> Engage reluctant readers with personalized stories</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>💼 Commercial Use</h2>
          <p>
            You retain full ownership of your created content.
          </p>
          <ul>
            <li><strong>100% Ownership:</strong> You own all rights to your storybooks</li>
            <li><strong>Sell Your Work:</strong> Publish and sell on Amazon KDP and other platforms</li>
            <li><strong>No Royalty Sharing:</strong> Keep all earnings from your book sales</li>
            <li><strong>Marketing Materials:</strong> Use your creations in promotional content</li>
            <li><strong>Client Work:</strong> Create storybooks for clients (small businesses, brands, etc.)</li>
            <li><strong>Social Media:</strong> Share your work on any platform</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>Need More Information?</h2>
          <p>
            Have questions about features or capabilities?
          </p>
          <p className="contact-info">
            <strong>Email:</strong> support@tinylittlefox.com<br />
            <strong>Website:</strong> <a href="https://tinylittlefox.com">https://tinylittlefox.com</a>
          </p>
          <p>
            We're happy to help you understand how our features can bring your storybook vision to life!
          </p>
        </section>

        <footer className="legal-footer">
          <p>Ready to explore all features? <a href="/register">Create your free account →</a></p>
        </footer>
      </div>
    </div>
  );
};

export default Features;
